// lib/models/source.ts
//
// Source = one reusable piece of onboarding material (uploaded PDF/image/text
// or registered URL). Owned by a unit; optionally promoted to the whole
// organisation. Mirrors the backend `reqres.SourceResponse` shape.

export type SourceKind = 'pdf' | 'image' | 'url' | 'text';

export interface Source {
    id: string;
    unit_id: string;
    org_id: string | null;   // null = unit-only; set = promoted to org-wide visibility
    kind: SourceKind;
    original_name: string;
    mime_type: string | null;
    size_bytes: number | null;
    url: string | null;           // for kind=url
    storage_key: string | null;   // for kind=file (used to build download URL)
    content_hash: string | null;  // SHA-256 hex; null for url sources
    is_promoted: boolean;          // convenience: org_id !== null
    // Number of modules that reference this source via module_sources.
    // 0 = safe to delete. > 0 = backend will refuse the delete; the UI
    // surfaces the count as a chip and the specific module list via
    // GET /api/sources/:id/modules.
    used_in_count: number;
    created_at: string;
    created_by: string;
    updated_at: string;
    updated_by: string;
}

// Slim module reference returned by `sourcesApi.listModulesUsingSource`.
// Just enough to render "this source is used in: ..." with deep-links
// into the module detail pages.
export interface ModuleRef {
    id: string;
    module_name: string;
}

export interface CreateSourceFromURLRequest {
    url: string;
    display_name?: string;
}

// Allowed file extensions for upload (matches backend whitelist —
// classifyUpload + textExtensions in services/source_service.go).
// Keep in lockstep with the backend: add here AND there together.
export const SOURCE_ALLOWED_EXTENSIONS = [
    // PDF + images
    '.pdf', '.png', '.jpg', '.jpeg', '.webp', '.gif',
    // Plain text / notes
    '.md', '.markdown', '.txt', '.rst', '.adoc', '.org', '.log',
    // Code — languages
    '.go',
    '.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs',
    '.py', '.pyw',
    '.rb',
    '.java', '.kt', '.kts',
    '.rs',
    '.swift',
    '.c', '.cpp', '.cc', '.cxx', '.h', '.hpp',
    '.cs',
    '.php',
    '.lua',
    '.scala',
    '.clj', '.cljs',
    '.ex', '.exs', '.erl',
    '.hs', '.ml', '.fs', '.fsx',
    '.dart',
    '.r',
    '.pl', '.pm',
    // Shell
    '.sh', '.bash', '.zsh', '.fish', '.ps1',
    // Web / markup
    '.html', '.htm',
    '.css', '.scss', '.sass', '.less',
    '.vue', '.svelte',
    // Config
    '.json', '.yaml', '.yml', '.toml',
    '.ini', '.env', '.conf', '.xml', '.properties',
    // Build / IaC
    '.gradle', '.cmake', '.mk',
    '.dockerfile', '.containerfile',
    '.tf', '.tfvars', '.hcl',
    // API schemas
    '.proto', '.graphql', '.gql',
    // Docs / patches
    '.tex', '.bib', '.diff', '.patch',
    // SQL + data
    '.sql', '.csv', '.tsv',
] as const;

// Files conventionally named WITHOUT an extension (Dockerfile,
// Makefile, etc.) or where the basename itself IS the type marker
// (.gitignore, .editorconfig, …). Matched against the lowercased
// basename — keep in lockstep with specialTextFilenames in
// services/source_service.go.
export const SOURCE_ALLOWED_SPECIAL_NAMES = new Set([
    'dockerfile', 'containerfile',
    'makefile', 'rakefile', 'gemfile', 'podfile', 'brewfile',
    'vagrantfile', 'jenkinsfile', 'procfile',
    '.gitignore', '.gitattributes', '.dockerignore', '.editorconfig',
    '.nvmrc', '.node-version', '.python-version', '.ruby-version', '.tool-versions',
    '.prettierrc', '.eslintrc', '.babelrc',
    'license', 'readme',
]);
export const SOURCE_MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB — matches backend cap

// Human-friendly label for a source kind, used in pickers + list rows.
export const SourceKindLabels: Record<SourceKind, string> = {
    pdf: 'PDF',
    image: 'Bild',
    url: 'Länk',
    text: 'Text',
};
